/**
 * 에러 피드백 컴포넌트
 *
 * 사용자가 직접 에러를 신고할 수 있는 UI 제공
 * Sentry의 User Feedback API를 사용하여 기술적 에러 로그와 매칭
 */
import * as Sentry from "@sentry/react-router";
import { AlertCircleIcon, CheckCircleIcon, SendIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "~/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/core/components/ui/dialog";
import { Label } from "~/core/components/ui/label";
import { Textarea } from "~/core/components/ui/textarea";
import {
  trackFeedbackButtonClick,
  trackFeedbackSubmitted,
} from "~/core/lib/analytics-events.client";

interface ErrorFeedbackProps {
  /**
   * 트리거 버튼 텍스트 (선택사항)
   */
  triggerText?: string;
  /**
   * 트리거 버튼 variant (선택사항)
   */
  triggerVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  /**
   * 사용자 정보 (선택사항)
   */
  user?: {
    id?: string;
    email?: string;
    name?: string;
  };
  /**
   * 추가 컨텍스트 (선택사항)
   */
  context?: Record<string, any>;
}

export function ErrorFeedback({
  triggerText = "문제가 있나요? 리포트 보내기",
  triggerVariant = "outline",
  user,
  context,
}: ErrorFeedbackProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feedback.trim()) {
      setError("피드백 내용을 입력해주세요.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // 사용자 정보 설정
      if (user?.id || user?.email) {
        Sentry.setUser({
          id: user.id,
          email: user.email || email,
          username: user.name || name,
        });
      } else if (email) {
        Sentry.setUser({
          email,
          username: name,
        });
      }

      // 추가 컨텍스트 설정
      if (context) {
        Sentry.setContext("사용자 리포트 컨텍스트", context);
      }

      // 현재 페이지 정보 추가
      Sentry.setContext("페이지 정보", {
        url: window.location.href,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });

      // 사용자 피드백을 Sentry 이벤트로 전송
      const eventId = Sentry.captureMessage("사용자 에러 리포트", {
        level: "info",
        tags: {
          type: "user_feedback",
          source: "error_feedback_component",
        },
        contexts: {
          feedback: {
            message: feedback,
            email: email || user?.email,
            name: name || user?.name,
          },
        },
      });

      // Sentry User Feedback API 사용
      if (eventId) {
        Sentry.captureFeedback({
          name: name || user?.name || "익명",
          email: email || user?.email || "",
          message: feedback,
          associatedEventId: eventId,
        });
      }

      // 성공 상태 표시
      setIsSuccess(true);
      setFeedback("");

      // 2초 후 다이얼로그 닫기
      setTimeout(() => {
        setIsOpen(false);
        setIsSuccess(false);
      }, 2000);
    } catch (err) {
      console.error("Error sending feedback:", err);
      setError("피드백 전송에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setFeedback("");
    setError(null);
    setIsSuccess(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} size="sm">
          <AlertCircleIcon className="mr-2 h-4 w-4" />
          {triggerText}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isSuccess ? "피드백이 전송되었습니다!" : "문제 리포트"}
          </DialogTitle>
          <DialogDescription>
            {isSuccess
              ? "소중한 피드백 감사합니다. 빠르게 확인하여 개선하겠습니다."
              : "발생한 문제를 자세히 설명해주시면 빠르게 해결하는 데 도움이 됩니다."}
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircleIcon className="h-16 w-16 text-green-500" />
            <p className="mt-4 text-center text-sm text-muted-foreground">
              개발팀이 확인하는 즉시 연락드리겠습니다.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 이름 입력 (선택사항) */}
            {!user?.name && (
              <div className="space-y-2">
                <Label htmlFor="name">이름 (선택사항)</Label>
                <input
                  id="name"
                  type="text"
                  placeholder="홍길동"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            )}

            {/* 이메일 입력 (선택사항) */}
            {!user?.email && (
              <div className="space-y-2">
                <Label htmlFor="email">이메일 (선택사항)</Label>
                <input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-muted-foreground">
                  이메일을 남겨주시면 해결 과정을 알려드립니다.
                </p>
              </div>
            )}

            {/* 피드백 입력 */}
            <div className="space-y-2">
              <Label htmlFor="feedback">
                문제 설명 <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="feedback"
                placeholder="예: 결제 버튼을 클릭했는데 아무 반응이 없어요."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={5}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                어떤 상황에서 문제가 발생했는지 자세히 설명해주세요.
              </p>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircleIcon className="h-4 w-4" />
                {error}
              </div>
            )}

            {/* 버튼들 */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                <XIcon className="mr-2 h-4 w-4" />
                취소
              </Button>
              <Button type="submit" disabled={isSubmitting || !feedback.trim()}>
                <SendIcon className="mr-2 h-4 w-4" />
                {isSubmitting ? "전송 중..." : "리포트 보내기"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * 간단한 에러 리포트 버튼 (고정 위치)
 * 
 * 사용 예시:
 * - 하단 우측에 고정된 플로팅 버튼
 * - 모든 페이지에서 접근 가능
 * - 풋터를 가리지 않도록 스마트 위치 조정
 */
export function FloatingErrorFeedback({
  user,
  context,
}: {
  user?: ErrorFeedbackProps["user"];
  context?: ErrorFeedbackProps["context"];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [bottomOffset, setBottomOffset] = useState(24); // 기본 24px (bottom-6)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feedback.trim()) {
      setError("피드백 내용을 입력해주세요.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (user?.id || user?.email) {
        Sentry.setUser({
          id: user.id,
          email: user.email || email,
          username: user.name || name,
        });
      } else if (email) {
        Sentry.setUser({
          email,
          username: name,
        });
      }

      if (context) {
        Sentry.setContext("사용자 리포트 컨텍스트", context);
      }

      Sentry.setContext("페이지 정보", {
        url: window.location.href,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });

      const eventId = Sentry.captureMessage("사용자 에러 리포트", {
        level: "info",
        tags: {
          type: "user_feedback",
          source: "floating_feedback_button",
        },
        contexts: {
          feedback: {
            message: feedback,
            email: email || user?.email,
            name: name || user?.name,
          },
        },
      });

      if (eventId) {
        Sentry.captureFeedback({
          name: name || user?.name || "익명",
          email: email || user?.email || "",
          message: feedback,
          associatedEventId: eventId,
        });
      }

      // Analytics 추적: 피드백 제출 완료
      trackFeedbackSubmitted(
        feedback.length,
        !!(email || user?.email)
      );

      setIsSuccess(true);
      setFeedback("");

      setTimeout(() => {
        setIsOpen(false);
        setIsSuccess(false);
      }, 2000);
    } catch (err) {
      console.error("Error sending feedback:", err);
      setError("피드백 전송에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setFeedback("");
    setError(null);
    setIsSuccess(false);
  };

  // 풋터 감지 및 버튼 위치 조정
  useEffect(() => {
    const handleScroll = () => {
      const footer = document.querySelector("footer");
      if (!footer) return;

      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const defaultBottom = 24; // bottom-6 = 1.5rem = 24px
      const gapFromFooter = 8; // 풋터와 버튼 사이 간격 (매우 가깝게)

      // 풋터가 화면에 보이기 시작하면
      if (footerRect.top < windowHeight) {
        // 화면 하단에서 풋터 상단까지의 거리
        const footerDistanceFromBottom = windowHeight - footerRect.top;
        // 버튼을 풋터 바로 위에 배치 (간격 최소화)
        setBottomOffset(footerDistanceFromBottom + gapFromFooter);
      } else {
        // 풋터가 안 보이면 기본 위치
        setBottomOffset(defaultBottom);
      }
    };

    // 초기 실행
    handleScroll();

    // 스크롤 이벤트 리스너
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <>
      {/* 작고 동그란 플로팅 버튼 - 풋터 위치 대응 */}
      <button
        onClick={() => {
          trackFeedbackButtonClick("floating_button");
          setIsOpen(true);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="fixed right-6 z-50 flex items-center gap-0 rounded-full bg-primary p-2.5 text-sm font-medium text-primary-foreground shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl hover:px-4 hover:gap-2"
        style={{ bottom: `${bottomOffset}px` }}
        aria-label="도움이 필요하신가요?"
      >
        <AlertCircleIcon className="h-4 w-4 flex-shrink-0" />
        <span className={`overflow-hidden whitespace-nowrap transition-all duration-300 ${isHovered ? "max-w-[200px] opacity-100" : "max-w-0 opacity-0"}`}>
          도움이 필요하신가요?
        </span>
      </button>

      {/* 다이얼로그 */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {isSuccess ? "소중한 의견 감사합니다!" : "무엇을 도와드릴까요?"}
            </DialogTitle>
            <DialogDescription>
              {isSuccess
                ? "빠르게 확인하여 개선하겠습니다."
                : "불편하신 점이나 개선이 필요한 부분을 알려주세요."}
            </DialogDescription>
          </DialogHeader>

          {isSuccess ? (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircleIcon className="h-16 w-16 text-green-500" />
              <p className="mt-4 text-center text-sm text-muted-foreground">
                소중한 의견을 반영하여 더 나은 서비스를 만들겠습니다.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {!user?.name && (
                <div className="space-y-2">
                  <Label htmlFor="name">이름 (선택사항)</Label>
                  <input
                    id="name"
                    type="text"
                    placeholder="홍길동"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
              )}

              {!user?.email && (
                <div className="space-y-2">
                  <Label htmlFor="email">이메일 (선택사항)</Label>
                  <input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <p className="text-xs text-muted-foreground">
                    이메일을 남겨주시면 처리 결과를 알려드립니다.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="feedback">
                  의견 <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="feedback"
                  placeholder="예: 결제 버튼이 작동하지 않아요."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={5}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  구체적으로 알려주실수록 빠르게 개선할 수 있습니다.
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircleIcon className="h-4 w-4" />
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  <XIcon className="mr-2 h-4 w-4" />
                  취소
                </Button>
                <Button type="submit" disabled={isSubmitting || !feedback.trim()}>
                  <SendIcon className="mr-2 h-4 w-4" />
                  {isSubmitting ? "전송 중..." : "보내기"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

